/**
 * Refresh access token via direct fetch. Used by the tRPC fetch wrapper
 * to avoid circular dependency (trpc -> fetch -> refresh -> trpc).
 *
 * Three behaviours that distinguish this module from a naive refresh:
 *
 * 1. **Refresh-token rotation.** When the server returns a new refresh
 *    token alongside the access token (sliding-window auth), it gets
 *    persisted alongside the new access token. The 7-day refresh clock
 *    slides forward with every refresh, so active users never time out.
 *
 * 2. **Terminal vs. transient classification.** `success: false` is no
 *    longer a single bucket. A 401/403 from `auth.refresh` is `terminal`
 *    (the refresh token is actually invalid; clear auth). A network
 *    error / 5xx is non-terminal (retry-able). Callers (the proactive
 *    scheduler, the tRPC fetch wrapper) act on this distinction —
 *    transient failures must NOT log the user out.
 *
 * 3. **In-flight de-duplication.** Concurrent calls (multiple parallel
 *    requests hitting 401, or a scheduled refresh racing with a manual
 *    one) all share a single in-flight promise. Avoids hammering the
 *    API and avoids one caller's success racing another's failure.
 */
import Constants from 'expo-constants';
import { setAccessToken } from './auth-store';
import { getToken, setToken } from './token-storage';
import { AUTH_KEYS } from '../contexts/AuthContext';

function getBaseUrl(): string {
  const fromConfig = Constants.expoConfig?.extra?.apiUrl;
  if (fromConfig) return fromConfig;
  if (typeof process !== 'undefined' && process.env.EXPO_PUBLIC_API_URL) {
    return process.env.EXPO_PUBLIC_API_URL;
  }
  return 'http://localhost:3000';
}

export interface RefreshResult {
  success: boolean;
  /** True only when the server told us the refresh token is invalid
   *  (HTTP 401/403). Network/5xx errors are non-terminal — retry. */
  terminal?: boolean | undefined;
  accessToken?: string | undefined;
  refreshToken?: string | undefined;
}

let inFlight: Promise<RefreshResult> | null = null;

export async function refreshAccessToken(): Promise<RefreshResult> {
  if (inFlight) return inFlight;
  inFlight = doRefresh().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function doRefresh(): Promise<RefreshResult> {
  const refreshToken = await getToken(AUTH_KEYS.refreshToken);
  if (!refreshToken) return { success: false, terminal: true };

  const baseUrl = getBaseUrl();
  const url = `${baseUrl}/trpc/auth.refresh?batch=1`;
  const body = JSON.stringify({
    0: { refreshToken },
  });

  let res: Response;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
    });
  } catch {
    // Network failure — retry-able.
    return { success: false, terminal: false };
  }

  if (res.status === 401 || res.status === 403) {
    // Server says the refresh token is invalid. Caller should clear auth.
    return { success: false, terminal: true };
  }
  if (!res.ok) {
    // 5xx, 502, etc. — server is having a moment, retry.
    return { success: false, terminal: false };
  }

  let data: unknown;
  try {
    data = await res.json();
  } catch {
    return { success: false, terminal: false };
  }
  const batch = Array.isArray(data) ? data : [data];
  const first = batch[0];
  const resultData = first?.result?.data;
  if (!resultData) return { success: false, terminal: false };

  const json =
    (resultData as { json?: { accessToken?: string; refreshToken?: string } | undefined}).json ??
    (resultData as { accessToken?: string | undefined; refreshToken?: string | undefined});
  const accessToken =
    json.accessToken ?? (resultData as { accessToken?: string | undefined}).accessToken;
  const newRefreshToken =
    json.refreshToken ?? (resultData as { refreshToken?: string | undefined}).refreshToken;
  if (!accessToken) return { success: false, terminal: false };

  setAccessToken(accessToken);
  await setToken(AUTH_KEYS.accessToken, accessToken);
  if (newRefreshToken) {
    await setToken(AUTH_KEYS.refreshToken, newRefreshToken);
  }
  return { success: true, accessToken, refreshToken: newRefreshToken };
}

/** Test-only: reset the in-flight singleton between tests. */
export function _resetInFlightForTests(): void {
  inFlight = null;
}
